import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Phone, User, Building2, ArrowRight, KeyRound, LogIn, UserPlus, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAgent } from '../../contexts/AgentContext';
import { useToast } from '../../hooks/use-toast';

/**
 * Agent sign-in & self-registration.
 *
 * SIGN IN: email → 6-digit OTP → verify → agent portal. No passwords.
 * REGISTER: name, email, phone, notes → status='pending_approval' → admin approves → can sign in.
 */
const AgentLogin = () => {
  const { requestLoginOtp, verifyLoginOtp, registerAgent } = useAgent();
  const nav = useNavigate();
  const loc = useLocation();
  const { toast } = useToast();
  const from = loc.state?.from || '/agent';

  const [mode, setMode] = useState('signin'); // signin | register
  const [step, setStep] = useState('email'); // email | otp | register-form | register-done
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast({ title: 'Enter your agent email' });
    setBusy(true);
    const r = await requestLoginOtp(email);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    toast({ title: 'Verification code sent', description: `Check ${email} for a 6-digit code.` });
    setStep('otp');
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast({ title: 'Enter the full 6-digit code' });
    setBusy(true);
    const r = await verifyLoginOtp(email, otp);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    toast({ title: 'Welcome back to the agent portal' });
    nav(from, { replace: true });
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast({ title: 'Full name and email are required' });
    setBusy(true);
    const r = await registerAgent(form);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    setStep('register-done');
  };

  const switchMode = (m) => () => {
    setMode(m);
    setStep(m === 'signin' ? 'email' : 'register-form');
    setOtp('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F0532] p-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="agent-portal-home-link">
          <svg viewBox="0 0 40 40" className="w-10 h-10">
            <circle cx="20" cy="20" r="20" fill="#FF5C39" />
            <path d="M12 22 L18 28 L28 14" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="leading-tight text-white">
            <div className="font-bold text-lg">Domestic Home Services</div>
            <div className="text-[10px] tracking-[0.22em] text-white/60 uppercase">Agent Portal</div>
          </div>
        </Link>

        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl">
          {/* Tab switcher */}
          {(step === 'email' || step === 'register-form') && (
            <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-[#FFF6EB] mb-6">
              <button
                data-testid="agent-tab-signin"
                type="button"
                onClick={switchMode('signin')}
                className={`h-10 rounded-full text-sm font-semibold transition-all ${mode === 'signin' ? 'bg-[#1F0068] text-white shadow' : 'text-[#1F0068]/70'}`}
              >Sign in</button>
              <button
                data-testid="agent-tab-register"
                type="button"
                onClick={switchMode('register')}
                className={`h-10 rounded-full text-sm font-semibold transition-all ${mode === 'register' ? 'bg-[#1F0068] text-white shadow' : 'text-[#1F0068]/70'}`}
              >Register</button>
            </div>
          )}

          {/* ---------- SIGN IN: email ---------- */}
          {mode === 'signin' && step === 'email' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><Building2 className="w-5 h-5" /></div>
                <div>
                  <h1 className="text-2xl font-bold text-[#1F0068]">Agent sign in</h1>
                  <p className="text-sm text-[#1F0068]/60">We'll email you a 6-digit code</p>
                </div>
              </div>
              <form onSubmit={sendOtp} className="space-y-4">
                <div>
                  <Label className="text-[#1F0068] font-semibold">Agent email</Label>
                  <div className="relative mt-2">
                    <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
                    <Input data-testid="agent-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-12 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                </div>
                <Button data-testid="agent-send-otp" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send code <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </form>
              <p className="mt-5 text-center text-xs text-[#1F0068]/60">
                Don't have an agent account yet? <button type="button" onClick={switchMode('register')} data-testid="agent-goto-register" className="underline font-semibold text-[#1F0068]">Apply here →</button>
              </p>
            </>
          )}

          {/* ---------- SIGN IN: otp ---------- */}
          {mode === 'signin' && step === 'otp' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><KeyRound className="w-5 h-5" /></div>
                <div><h1 className="text-2xl font-bold text-[#1F0068]">Enter your code</h1><p className="text-sm text-[#1F0068]/60">Sent to <b>{email}</b></p></div>
              </div>
              <form onSubmit={submitOtp}>
                <Input data-testid="agent-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} maxLength={6} placeholder="6-digit code" className="h-14 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-center text-2xl tracking-widest" />
                <Button data-testid="agent-verify-otp" type="submit" disabled={busy} className="mt-3 w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verify & sign in <LogIn className="w-4 h-4 ml-1" /></>}
                </Button>
                <button data-testid="agent-otp-back" type="button" onClick={() => { setOtp(''); setStep('email'); }} className="mt-3 text-xs text-[#1F0068]/70 underline block mx-auto">Back</button>
              </form>
            </>
          )}

          {/* ---------- REGISTER: form ---------- */}
          {mode === 'register' && step === 'register-form' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><UserPlus className="w-5 h-5" /></div>
                <div>
                  <h1 className="text-2xl font-bold text-[#1F0068]">Apply to be an agent</h1>
                  <p className="text-sm text-[#1F0068]/60">Admin will review and approve your account</p>
                </div>
              </div>
              <form onSubmit={submitRegister} className="space-y-3">
                <div>
                  <Label className="text-[#1F0068] font-semibold">Full name</Label>
                  <div className="relative mt-2"><User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="agent-reg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Work email</Label>
                  <div className="relative mt-2"><Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="agent-reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Phone (UK)</Label>
                  <div className="relative mt-2"><Phone className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="agent-reg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07700 900 123" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">About you <span className="text-[#1F0068]/50 font-normal">(optional)</span></Label>
                  <Textarea data-testid="agent-reg-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Briefly describe your experience — this helps the admin review your application." className="mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                </div>
                <Button data-testid="agent-reg-submit" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit application <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
                <p className="text-[11px] text-[#1F0068]/60 leading-relaxed">You'll receive an email once your application is approved. Then you can sign in using the OTP sent to this email.</p>
              </form>
            </>
          )}

          {/* ---------- REGISTER: done ---------- */}
          {mode === 'register' && step === 'register-done' && (
            <div data-testid="agent-reg-success" className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-[#00b67a]/15 text-[#00b67a] flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8" /></div>
              <h1 className="text-2xl font-bold text-[#1F0068] mb-2">Application received</h1>
              <p className="text-sm text-[#1F0068]/70 mb-6">Thanks {form.name.split(' ')[0]}! Our admin team will review your application and email you at <b>{form.email}</b> once it's approved. Approval typically takes 1–2 business days.</p>
              <Button data-testid="agent-reg-back-signin" onClick={() => { setMode('signin'); setStep('email'); setEmail(form.email); }} className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white">Back to sign in</Button>
            </div>
          )}
        </div>
        <div className="text-center mt-6"><Link to="/" className="text-xs text-white/60 hover:text-white">← Back to customer site</Link></div>
      </div>
    </div>
  );
};

export default AgentLogin;
